/**
 * Heap tracking for Ask Scan AI.
 * Event names and PascalCase props match existing Heap conventions in this app.
 *
 * Privacy: never send ChatId (DB join key to symptom messages) or free-text
 * health content. Funnel events share an opaque browser session id only.
 */

export const AI_TRIAGE_BLOCK_REASONS = {
  RATE_LIMITED: "rate_limited",
  SPEND: "spend",
  BUSY: "busy",
  ATTACHMENT: "attachment",
  RECAPTCHA: "recaptcha",
  BOOTSTRAP: "bootstrap_failed",
};

/** Shared ref / utm_source value stamped on recommendation CTAs. */
export const AI_TRIAGE_REF = "ai_triage";

/** Named campaign on the same helper. Distinct from utm_source. */
export const AI_TRIAGE_UTM_CAMPAIGN = "ask_scan";

const AI_TRIAGE_UTM_MEDIUM = "recommendation";
const ANALYTICS_SESSION_STORAGE_KEY = "ai_triage_analytics_sid";

function analyticsSessionId() {
  try {
    let id = sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function baseProps(extra = {}) {
  return {
    AnalyticsSessionId: analyticsSessionId(),
    ...extra,
  };
}

function cleanProperties(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
}

/**
 * Append attribution query params to a recommendation CTA path.
 * ProductId/Cta are catalog enums only — never free-text health content.
 */
export function withAiTriageAttribution(path, { productId, cta } = {}) {
  if (!path) {
    return path;
  }

  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set("ref", AI_TRIAGE_REF);
    url.searchParams.set("utm_source", AI_TRIAGE_REF);
    url.searchParams.set("utm_medium", AI_TRIAGE_UTM_MEDIUM);
    url.searchParams.set("utm_campaign", AI_TRIAGE_UTM_CAMPAIGN);
    if (productId) {
      url.searchParams.set("utm_content", productId);
    }
    if (cta) {
      url.searchParams.set("utm_term", cta);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

/**
 * Stamp Heap user properties so later Search/Purchase events can be
 * attributed to Ask Scan AI without a dedicated purchase event.
 */
export function stampAiTriageInfluence({ productId, cta } = {}) {
  if (!window.heap?.addUserProperties) {
    return;
  }

  const properties = cleanProperties({
    AiTriageInfluenced: true,
    AiTriageProductId: productId,
    AiTriageCta: cta,
    AiTriageAnalyticsSessionId: analyticsSessionId(),
  });

  window.heap.addUserProperties(properties);
}

export function trackAiTriage(eventName, properties = {}) {
  if (!window.heap?.track) {
    return;
  }

  window.heap.track(eventName, cleanProperties(properties));
}

export function trackAiTriageOpened({ source } = {}) {
  trackAiTriage(
    "AiTriageOpened",
    baseProps({
      Source: source || "mode",
    })
  );
}

export function trackAiTriageClosed({
  hasRecommendation,
  durationSeconds,
} = {}) {
  trackAiTriage(
    "AiTriageClosed",
    baseProps({
      HasRecommendation: Boolean(hasRecommendation),
      DurationSeconds: durationSeconds,
    })
  );
}

export function trackAiTriageChatCreated() {
  trackAiTriage("AiTriageChatCreated", baseProps());
}

export function trackAiTriageMessageSent({
  messageIndex,
  hasAttachment,
  via,
} = {}) {
  trackAiTriage(
    "AiTriageMessageSent",
    baseProps({
      MessageIndex: messageIndex,
      HasAttachment: Boolean(hasAttachment),
      Via: via || "typed",
    })
  );
}

/**
 * @param {{ via?: string, chipKey?: string, chipPosition?: number }} [detail]
 */
export function trackAiTriageSuggestionClicked({
  via,
  chipKey,
  chipPosition,
} = {}) {
  // Do not send chip/suggestion free text — can contain special-category health data.
  // ChipKey and ChipPosition are set for starter chips only.
  trackAiTriage(
    "AiTriageSuggestionClicked",
    baseProps({
      Via: via || "suggestion",
      ChipKey: chipKey,
      ChipPosition: chipPosition,
    })
  );
}

// One event per chip so Heap can divide clicks by impressions per ChipKey.
export function trackAiTriageStarterChipsShown(chipKeys = []) {
  chipKeys.forEach((chipKey, index) => {
    trackAiTriage(
      "AiTriageStarterChipShown",
      baseProps({ ChipKey: chipKey, ChipPosition: index + 1 })
    );
  });
}

export function trackAiTriageRecommendationShown({
  productId,
  messageIndex,
  isFirstRecommendation,
} = {}) {
  // ProductId is a curated catalog enum. Never send body part / area free text.
  trackAiTriage(
    "AiTriageRecommendationShown",
    baseProps({
      ProductId: productId,
      HasRecommendation: true,
      MessageIndex: messageIndex,
      IsFirstRecommendation: Boolean(isFirstRecommendation),
    })
  );
}

export function trackAiTriageBookClicked({ productId, cta } = {}) {
  stampAiTriageInfluence({ productId, cta });
  trackAiTriage(
    "AiTriageBookClicked",
    baseProps({
      ProductId: productId,
      Cta: cta,
    })
  );
}

export function trackAiTriageFeedback({ rating } = {}) {
  trackAiTriage(
    "AiTriageFeedback",
    baseProps({
      Rating: rating,
    })
  );
}

export function trackAiTriageBlocked({ reason, stage } = {}) {
  trackAiTriage(
    "AiTriageBlocked",
    baseProps({
      Reason: reason,
      Stage: stage,
    })
  );
}

export function trackAiTriageGuardrailBlocked() {
  trackAiTriage("AiTriageGuardrailBlocked", baseProps());
}

export const GUARDRAIL_KIND_EMERGENCY = "emergency";

export function trackAiTriageHelpEmailClicked() {
  trackAiTriage(
    "AiTriageHelpEmailClicked",
    baseProps({
      GuardrailKind: GUARDRAIL_KIND_EMERGENCY,
    })
  );
}

export function inferBlockReasonFromNotice(text) {
  const notice = (text || "").toString().toLowerCase();
  if (!notice) {
    return null;
  }
  if (notice.includes("sending messages a bit quickly")) {
    return AI_TRIAGE_BLOCK_REASONS.RATE_LIMITED;
  }
  if (notice.includes("temporarily at capacity")) {
    return AI_TRIAGE_BLOCK_REASONS.SPEND;
  }
  if (notice.includes("still working on your last message")) {
    return AI_TRIAGE_BLOCK_REASONS.BUSY;
  }
  if (notice.includes("file review is temporarily unavailable")) {
    return AI_TRIAGE_BLOCK_REASONS.ATTACHMENT;
  }
  return null;
}
